import { useEffect, useState } from 'react';
import { api } from '../api';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';

interface Props { session: Session; }

interface RollDetail {
  table_id: string;
  entry_index: number;
  pity_triggered: boolean;
  entry_type: string;
  ref_key: string;
  amount: { value: number; decimal_places: number };
}

interface LootProbability {
  entry_index: number;
  entry_type: string;
  ref_key: string;
  probability: number;
  pity_guaranteed_within: number | null;
}

export default function Loot({ session }: Props) {
  const [tables, setTables] = useState<string[]>([]);
  const [selectedTable, setSelectedTable] = useState<string>('');
  const [iterations, setIterations] = useState(10);
  const [rollResults, setRollResults] = useState<RollDetail[]>([]);
  const [probabilities, setProbabilities] = useState<LootProbability[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Load available loot tables from config entities
  useEffect(() => {
    api
      .listEntities(session.gameId, { package: 'loot', entity_type: 'loot_table', status: 'published' })
      .then(rows => {
        setTables(rows.map(r => r.entity_key));
        if (rows.length > 0 && !selectedTable) {
          setSelectedTable(rows[0].entity_key);
        }
      })
      .catch(e => setError(e.message));
  }, [session.gameId]);

  const rollTable = async () => {
    if (!selectedTable) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/v1/games/${session.gameId}/modules/loot/roll/${selectedTable}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ iterations }),
        },
      );
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setRollResults(data.all_details ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const loadPreview = async () => {
    if (!selectedTable) return;
    setError(null);
    try {
      const res = await fetch(
        `/api/v1/games/${session.gameId}/modules/loot/preview/${selectedTable}`,
      );
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setProbabilities(data ?? []);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  useEffect(() => {
    if (selectedTable) loadPreview();
  }, [selectedTable, session.gameId]);

  // Aggregate roll results for summary
  const summary = new Map<string, { count: number; totalAmount: number }>();
  for (const r of rollResults) {
    const key = r.entry_type === 'nothing' ? '(nothing)' : `${r.entry_type}:${r.ref_key}`;
    const prev = summary.get(key) ?? { count: 0, totalAmount: 0 };
    prev.count += 1;
    prev.totalAmount += r.amount.value;
    summary.set(key, prev);
  }

  return (
    <Container>
      <PageHeader
        title="Loot Tables"
        subtitle="Test-roll tables, preview probabilities, and tune drop rates."
      />

      {error && <div className="error-banner">{error}</div>}

      {/* Table selector + roll controls */}
      <div className="card mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-3 items-end">
          <div>
            <label className="block text-[11.5px] uppercase tracking-wider font-medium text-fg-dim mb-1.5">
              Loot table
            </label>
            <select
              className="input"
              value={selectedTable}
              onChange={e => setSelectedTable(e.target.value)}
            >
              {tables.length === 0 && <option value="">No tables published</option>}
              {tables.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11.5px] uppercase tracking-wider font-medium text-fg-dim mb-1.5">
              Iterations
            </label>
            <input
              className="input"
              type="number"
              min={1}
              max={10000}
              value={iterations}
              onChange={e => setIterations(parseInt(e.target.value, 10) || 1)}
              style={{ width: 120 }}
            />
          </div>
          <button className="btn btn-primary" disabled={busy || !selectedTable} onClick={rollTable}>
            {busy ? 'Rolling…' : `Roll ${iterations}x`}
          </button>
          <button className="btn" disabled={!selectedTable} onClick={loadPreview}>
            Preview
          </button>
        </div>
        <div className="text-xs text-fg-muted mt-3">
          Create loot tables in <strong>Config Explorer</strong> (package: loot, type: loot_table),
          publish, then roll here.
        </div>
      </div>

      {/* Probability preview */}
      {probabilities.length > 0 && (
        <>
          <h2 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-fg-dim mb-3">
            Drop probabilities — {selectedTable}
          </h2>
          <div className="table-wrap mb-5">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Type</th>
                  <th>Ref</th>
                  <th>Probability</th>
                  <th>Pity within</th>
                </tr>
              </thead>
              <tbody>
                {probabilities.map(p => (
                  <tr key={p.entry_index}>
                    <td className="text-fg-muted">{p.entry_index}</td>
                    <td>
                      <span className={`badge ${p.entry_type === 'nothing' ? 'badge-muted' : 'badge-published'}`}>
                        {p.entry_type}
                      </span>
                    </td>
                    <td className="font-medium">{p.ref_key || '—'}</td>
                    <td className="font-mono tabular-nums">
                      {(p.probability * 100).toFixed(1)}%
                    </td>
                    <td className="text-fg-dim">
                      {p.pity_guaranteed_within ? `${p.pity_guaranteed_within} rolls` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Roll results summary */}
      {rollResults.length > 0 && (
        <>
          <h2 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-fg-dim mb-3">
            Roll results — {rollResults.length} drops from {iterations} iteration(s)
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 mb-5">
            {[...summary.entries()].map(([key, { count, totalAmount }]) => (
              <div key={key} className="card hover:border-line-strong transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-fg-dim">
                    {key}
                  </div>
                  <div className="text-xs text-fg-muted">
                    {((count / rollResults.length) * 100).toFixed(1)}%
                  </div>
                </div>
                <div className="text-[28px] font-semibold tabular-nums tracking-tight">
                  {count}
                </div>
                {totalAmount > 0 && key !== '(nothing)' && (
                  <div className="text-xs text-fg-dim mt-1">
                    total: {totalAmount.toLocaleString()}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Raw detail table */}
          <details className="mb-5">
            <summary className="text-xs text-fg-muted cursor-pointer hover:text-fg-dim">
              Show raw roll details ({rollResults.length} entries)
            </summary>
            <div className="table-wrap mt-2">
              <table>
                <thead>
                  <tr>
                    <th>Table</th>
                    <th>Type</th>
                    <th>Ref</th>
                    <th>Amount</th>
                    <th>Pity?</th>
                  </tr>
                </thead>
                <tbody>
                  {rollResults.slice(0, 100).map((r, i) => (
                    <tr key={i}>
                      <td className="text-fg-muted text-xs">{r.table_id}</td>
                      <td>
                        <span className={`badge ${r.entry_type === 'nothing' ? 'badge-muted' : 'badge-published'}`}>
                          {r.entry_type}
                        </span>
                      </td>
                      <td className="font-medium">{r.ref_key || '—'}</td>
                      <td className="font-mono tabular-nums">{r.amount.value}</td>
                      <td>{r.pity_triggered ? '✓' : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      )}

      {tables.length === 0 && (
        <div className="empty">
          <div className="text-fg-2 font-medium mb-1">No loot tables published</div>
          <div className="text-xs text-fg-muted">
            Go to Config Explorer → Loot → Loot table → + New → Publish
          </div>
        </div>
      )}
    </Container>
  );
}
